import Big from 'big.js';
import { Direction, type MarketDataSnapshot, type MarketDataSnapshotLevel } from '../../types';
import type { PropType } from '../../utils/types';

const DEFAULT_HEADER_HEIGHT = 68;
const DEFAULT_ROW_MIN_HEIGHT = 20;

export function calculatePriceLadderDepth(
  height: number,
  headerHeight = DEFAULT_HEADER_HEIGHT,
  rowMinHeight = DEFAULT_ROW_MIN_HEIGHT
) {
  return Math.max(1, Math.floor((height - headerHeight * 2 - rowMinHeight * 2) / rowMinHeight / 2));
}

export const formatMarketData = (marketData: MarketDataSnapshot, depth: number, increment: string): StepData => {
  const topBid = marketData.Bids[0];
  const topOffer = marketData.Offers[0];

  // Calulate spread in currency and bps
  const spread =
    topOffer?.Price != null && topBid?.Price != null ? Big(topOffer.Price).minus(topBid.Price).toFixed() : undefined;
  const spreadBps =
    spread != null ? Big(spread).div(Big(topOffer.Price).plus(topBid.Price).div(2)).times(10000).toFixed() : undefined;

  // Get evenly spaced price ladder steps from the top bid/offer using the requested increment.
  // Also adds AccSize to each step.
  const { steps: bids, maxSize: bidsMaxSize } = getPriceLadderSteps(marketData.Bids, depth, increment, Direction.Desc);
  const { steps: offers, maxSize: offersMaxSize } = getPriceLadderSteps(
    marketData.Offers,
    depth,
    increment,
    Direction.Asc
  );
  offers.reverse();

  const maxSize = Math.max(bidsMaxSize, offersMaxSize);
  const activeMarkets = Object.values<PropType<NonNullable<typeof marketData.Markets>, 'Markets'>>(
    marketData.Markets ?? {}
  ).filter(v => v.Status !== 'Stale');
  return {
    topBid,
    topOffer,
    spread,
    spreadBps,
    offers,
    bids,
    maxSize,
    symbol: marketData.Symbol,
    status: marketData.Status != null ? marketData.Status : activeMarkets.length > 0 ? 'Online' : 'Stale',
  };
};

export function getPriceLadderSteps(
  levels: MarketDataSnapshotLevel[],
  depth: number,
  increment: string,
  direction: Direction
) {
  let levelIndex = 0;
  let maxSize = 0;
  const steps = Array.from<Step>({ length: depth }).reduce(
    (steps: (Step | undefined)[], _: Step | undefined, index: number) => {
      const level = levels[levelIndex];
      const prevStep = steps[index - 1];
      const price =
        prevStep != null
          ? direction === Direction.Asc
            ? Big(prevStep.Price ?? '0').plus(increment || '0')
            : Big(prevStep.Price ?? '0').minus(increment || '0')
          : undefined;
      let nextStep: Step | undefined = undefined;

      // Check if new price appears before next level with liquidity
      if (
        prevStep != null &&
        price != null &&
        (level == null || (direction === Direction.Asc ? price?.lt(level.Price ?? '0') : price?.gt(level.Price ?? '0')))
      ) {
        // Add filler step
        nextStep = {
          ...prevStep,
          Size: '0',
          Price: price?.toFixed(),
        };
      } else if (level != null) {
        // Add step with liquidity from WS result
        levelIndex += 1;
        nextStep = {
          ...level,
          AccSize: Big(prevStep?.AccSize || 0)
            .add(level.Size!)
            .toFixed(),
        };
      }
      maxSize = Math.max(maxSize, parseFloat(nextStep?.AccSize ?? '0'));
      return [...steps, nextStep];
    },
    []
  );
  return {
    steps,
    maxSize,
  };
}

export function getPriceLadderSnapshotLevels(
  marketData: MarketDataSnapshot,
  depth: number
): PriceLadderSnapshotLevel[] {
  let accBidSize = Big(0);
  let accOfferSize = Big(0);

  return Array.from({ length: depth }, (_, index) => {
    const bid = marketData.Bids[index];
    const offer = marketData.Offers[index];

    accBidSize = accBidSize.add(bid?.Size ?? '0');
    accOfferSize = accOfferSize.add(offer?.Size ?? '0');

    return {
      bid: bid ? { ...bid, AccSize: accBidSize.toFixed() } : { AccSize: accBidSize.toFixed() },
      offer: offer ? { ...offer, AccSize: accOfferSize.toFixed() } : { AccSize: accOfferSize.toFixed() },
    };
  });
}

export type StepData = {
  topBid?: MarketDataSnapshotLevel;
  topOffer?: MarketDataSnapshotLevel;
  spread?: string;
  spreadBps?: string;
  offers: (Step | undefined)[];
  bids: (Step | undefined)[];
  maxSize: number;
  symbol: string;
  status: string;
};

export interface Step extends MarketDataSnapshotLevel {
  AccSize: string;
}

export interface PriceLadderSnapshotLevel {
  bid: Partial<MarketDataSnapshotLevel> & { AccSize: string };
  offer: Partial<MarketDataSnapshotLevel> & { AccSize: string };
}
